/* eslint-disable no-console */

import { initAdobeDataLayer } from './init';
import { logPageLoadEventToXDM } from './xdm.helpers';
import { createListingPageLoadEvent } from './pdt/listing.helper';
import type { SearchArgumentsForXdm } from './pdt/pdtToXdm.mapper';

type InitialState = {
  templateData?: {
    templateName?: string;
    template_type?: string;
    metaTitle?: string;
    sampleUrl?: string;
  };
  code?: string;
  name?: string;
  id?: string | number;
  operatorName?: string;
  routeCategoryId?: string | number;
  routeOperatorId?: string | number;
  sourceCityData?: { code?: string; name?: string };
  destinationCityData?: { code?: string; name?: string };
  routeData?: {
    sourceCityData?: { name?: string };
    destinationCityData?: { name?: string };
  };
  cityData?: { name?: string };
  operatorData?: { operatorName?: string };
  categoryData?: { friendlyCategoryName?: string; categoryName?: string };
};

declare global {
  interface Window {
    __XDM_PAGELOAD_SENT__?: Set<string>;
  }
}

const getDedupeSet = (): Set<string> => {
  if (!window.__XDM_PAGELOAD_SENT__) {
    window.__XDM_PAGELOAD_SENT__ = new Set();
  }
  return window.__XDM_PAGELOAD_SENT__;
};

const startOfTomorrow = (): Date => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getSearchTypeFromTemplateName = (
  templateName?: string,
): string | null => {
  // Keep in sync with SEO template naming used across the app (see `App.js`)
  // and sitemap cache keys (see `server/renderSitemapXML.js`).
  switch (templateName) {
    case 'city':
      return 'CITY';
    case 'route':
      return 'CITY_CITY';
    case 'operator':
      return 'BUS_OPERATOR';
    case 'route_category':
      return 'CITY_CITY_BUS_TYPE';
    case 'route_operator':
      return 'CITY_CITY_BUS_OPERATOR';
    case 'city_operator':
      return 'CITY_BUS_OPERATOR';
    case 'city_category':
      return 'CITY_BUS_TYPE';
    default:
      return null;
  }
};

export const getXDMPageNameFromTemplateName = (templateName?: string): string | null => {

  switch (templateName) {
    case 'city':
      return 'city';
    case 'route':
      return 'route';
    case 'operator':
      return 'operator';
    case 'route_category':
      return 'route_category';
    case 'route_operator':
      return 'route_operator';
    case 'city_operator':
      return 'city_operator';
    case 'city_category':
      return 'city_category';
    case 'rtc_route_type':
      return 'rtc_route_type';
    case 'rtc_type':
      return 'rtc_type';
    case 'city_to_airport':
      return 'city_to_airport';
    case 'city_to_airport_category':
      return 'city_to_airport_category';
    case 'bus_stand':
      return 'bus_stand';
    case 'category':
      return 'category';
    case 'all':
      return 'all';
    default:
      return null;
  }
};
export const createSeoPageLoadEvent = ({
  initialState,
}: {
  initialState: InitialState | undefined;
}) => {
  const fromName =
    initialState?.sourceCityData?.name ||
    initialState?.routeData?.sourceCityData?.name ||
    null;
  const toName =
    initialState?.destinationCityData?.name ||
    initialState?.routeData?.destinationCityData?.name ||
    null;

  const fromCode = initialState?.sourceCityData?.code || null;
  const toCode = initialState?.destinationCityData?.code || null;

  // Keep it simple: always use tomorrow's date (start of day).
  const date = startOfTomorrow();
  const searchType = getSearchTypeFromTemplateName(
    initialState?.templateData?.templateName,
  );
    const xdmPageName = getXDMPageNameFromTemplateName(initialState?.templateData?.templateName);
    if (!xdmPageName) {
      return null;
    }
  const searchArguments: SearchArgumentsForXdm = {
    from: {
      code: fromCode,
      name: fromName,
    },
    to: {
      code: toCode,
      name: toName,
    },
    date,
    searchType,
    tripType: 'one-way',
    queryParams: {},
    pageName: xdmPageName,
  };

  // Build payload exactly like live listing payload using PDT helpers.
  return createListingPageLoadEvent({ searchArguments });
};

export const logSeoPageLoadFromInitialState = async (
  initialState: InitialState | undefined,
): Promise<boolean> => {
  try {
    if (typeof window === 'undefined') return false;

    const href = window.location.href;
    const dedupe = getDedupeSet();
    if (dedupe.has(href)) return false;
    dedupe.add(href);

    // Bootstrap script (async). Logging can happen immediately; wrapper is best-effort.
    initAdobeDataLayer();

    const event = createSeoPageLoadEvent({ initialState });
    return await logPageLoadEventToXDM({
      event,
    });
  } catch (e) {
    return false;
  }
};
